/**
 * Minimal drop-in query cache — no external dependency.
 *
 * Why not SWR / TanStack Query?
 * ─────────────────────────────
 * Pout's bundle is already tight. We only need three things:
 *   1. Stale-while-revalidate (return cached data instantly, refresh in bg)
 *   2. Request coalescing (multiple components asking for /dashboard once)
 *   3. Cache-key invalidation after writes
 * That's ~120 lines below. TanStack Query would add ~40 KB gzipped for the same.
 *
 * Public API
 * ──────────
 *   useQuery(key, fetcher, { staleMs = 30_000, keepStaleOnError = true })
 *     → { data, error, isLoading, isStale, refetch }
 *
 *   invalidate(prefix)         // drop all entries whose key startsWith prefix
 *   invalidateAll()
 *   prefetch(key, fetcher, opts)
 *
 * Caching semantics
 * ─────────────────
 *   Fresh (age < staleMs)  → return cached, no network call
 *   Stale (older)          → return cached AND fire background refresh
 *   Missing                → subscribe to (or start) an in-flight fetch
 *   Error while has cache  → return last-known-good data with `error` set
 *                            (keepStaleOnError=true), so the UI never blanks
 */
import { useCallback, useEffect, useRef, useState } from "react";

const store = new Map();       // key → { data, error, ts }
const generations = new Map();
const inflight = new Map();    // key → Promise
const listeners = new Map();   // key → Set<callback>

function notify(key) {
  const set = listeners.get(key);
  if (!set) return;
  set.forEach((cb) => {
    try { cb(); } catch { /* swallow */ }
  });
}

function subscribe(key, cb) {
  if (!listeners.has(key)) listeners.set(key, new Set());
  listeners.get(key).add(cb);
  return () => {
    const set = listeners.get(key);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) listeners.delete(key);
  };
}

async function runFetch(key, fetcher) {
  if (inflight.has(key)) return inflight.get(key);
  const generation = generations.get(key) || 0;
  const p = (async () => {
    try {
      const data = await fetcher();
      if ((generations.get(key) || 0) !== generation) return data;
      store.set(key, { data, error: null, ts: Date.now() });
      notify(key);
      return data;
    } catch (err) {
      if ((generations.get(key) || 0) !== generation) throw err;
      const prev = store.get(key);
      // Preserve the previous good data; expose the error alongside.
      store.set(key, {
        data: prev ? prev.data : undefined,
        error: err,
        ts: prev ? prev.ts : Date.now(),
      });
      notify(key);
      throw err;
    } finally {
      if ((generations.get(key) || 0) === generation) inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export function invalidate(prefix) {
  if (!prefix) return;
  for (const k of new Set([...store.keys(), ...inflight.keys(), ...listeners.keys()])) {
    if (k.startsWith(prefix)) {generations.set(k,(generations.get(k)||0)+1);store.delete(k);inflight.delete(k);}
  }
  // Notify all subscribers under the prefix so they re-fetch.
  for (const k of Array.from(listeners.keys())) {
    if (k.startsWith(prefix)) notify(k);
  }
}

export function invalidateAll() {
  for (const k of new Set([...store.keys(), ...inflight.keys(), ...listeners.keys()])) {generations.set(k,(generations.get(k)||0)+1);}
  store.clear();inflight.clear();
  for (const k of Array.from(listeners.keys())) notify(k);
}

export function prefetch(key, fetcher) {
  if (!key || typeof fetcher !== "function") return;
  const entry = store.get(key);
  if (entry && Date.now() - entry.ts < 5_000) return; // recent enough
  runFetch(key, fetcher).catch(() => {});
}

/**
 * useQuery — SWR-style hook with cache-first, single-flight, stale-safe UX.
 */
export function useQuery(key, fetcher, opts = {}) {
  const { staleMs = 30_000, enabled = true, keepStaleOnError = true } = opts;
  const [, force] = useState(0);
  const rerender = useCallback(() => force((n) => n + 1), []);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!key || !enabled) return undefined;
    const unsub = subscribe(key, () => {
      rerender();
      if (!store.has(key)) runFetch(key, () => fetcherRef.current()).catch(() => {});
    });
    const entry = store.get(key);
    const age = entry ? Date.now() - entry.ts : Infinity;
    if (!entry || age >= staleMs) {
      runFetch(key, () => fetcherRef.current()).catch(() => {});
    }
    return unsub;
  }, [key, enabled, staleMs, rerender]);

  const entry = key ? store.get(key) : undefined;
  const isFetching = key ? inflight.has(key) : false;
  const age = entry ? Date.now() - entry.ts : Infinity;
  const isStale = age >= staleMs;

  const refetch = useCallback(() => {
    if (!key) return Promise.resolve();
    return runFetch(key, () => fetcherRef.current()).catch(() => {});
  }, [key]);

  const data = entry ? entry.data : undefined;
  const error = entry && !keepStaleOnError ? entry.error : (entry?.error && data === undefined ? entry.error : null);

  return {
    data,
    error,
    isLoading: data === undefined && isFetching,
    isFetching,
    isStale,
    hasStaleData: data !== undefined && (entry?.error || isStale),
    refetch,
  };
}

// Debounce helper (used by search inputs to avoid hammering Amelia while typing).
export function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}
